import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import {
  useCreateJobScheduleMutation,
  useListJobSchedulesWithInputQuery,
  useRunJobScheduleNowMutation,
  useUpdateJobScheduleMutation,
} from "~/ui-rtk/api/jobsApi"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import useAccess from "~/ui-rtk/hooks/access"
import { BROWSER_TZ, TIMEZONE_OPTIONS } from "~/ui-rtk/utils/timezone-options"
import {
  type Cadence,
  DEFAULT_CADENCE,
  type Frequency,
  fromCron,
  toCron,
} from "../utils/cadence-cron"

const JOB_TYPE = "custom-report-slack"
const DEFAULT_TIME = "09:00"

/** Mirrors `CustomReportSlackInput` in the API's custom-report-slack job handler. */
type ReportWindow = "yesterday" | "last_7d" | "last_30d" | "last_quarter"
type ReportCompare = "none" | "prior_period" | "yoy" | "both"

const DEFAULT_COMPARE: ReportCompare = "prior_period"

interface SlackNotificationInput {
  reportId: string
  window: ReportWindow
  timezone: string
  slackChannelId?: string
  compare?: ReportCompare
}

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
]

const WINDOW_OPTIONS = [
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7d", label: "Last 7 days" },
  { value: "last_30d", label: "Last 30 days" },
  { value: "last_quarter", label: "Last quarter" },
]

const COMPARE_OPTIONS = [
  { value: "prior_period", label: "Previous period" },
  { value: "yoy", label: "Same period last year" },
  { value: "both", label: "Previous period and last year" },
  { value: "none", label: "No comparison" },
]

const DAY_OPTIONS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
].map((label, value) => ({ value: String(value), label }))

/**
 * Slack delivery for one custom report. The REPORT stays shape-only (measures,
 * no dates); everything time-varying — cadence, window, comparison, channel —
 * is captured here when notifications are enabled, and stored on a plain
 * `JobSchedule`. One report can therefore feed several schedules (a daily and a
 * quarterly post) without duplicating the report itself.
 */
export default function SlackNotificationSection({
  reportId,
}: {
  reportId: string
}) {
  const { data: schedules, isFetching } = useListJobSchedulesWithInputQuery({
    type: JOB_TYPE,
  })
  const existing = useMemo(
    () =>
      schedules?.find(
        schedule =>
          (schedule.input as SlackNotificationInput | undefined)?.reportId ===
          reportId,
      ),
    [schedules, reportId],
  )
  const existingInput = existing?.input as SlackNotificationInput | undefined

  const [createSchedule, { isLoading: isCreating }] =
    useCreateJobScheduleMutation()
  const [updateSchedule, { isLoading: isUpdating }] =
    useUpdateJobScheduleMutation()
  const [runScheduleNow, { isLoading: isRunningNow }] =
    useRunJobScheduleNowMutation()
  const isSaving = isCreating || isUpdating

  // CHOOSING a channel is super-admin-only server-side (it can address ANY channel
  // the Marathon bot sits in, including another company's), so the input is hidden
  // rather than advertising a capability the API refuses. Everyone else still gets
  // told WHERE their report lands: staying silent reads as "the company default"
  // even when a super admin pointed it somewhere else. A channel already set rides
  // along in `channelId` and is preserved on save.
  const { isSuperAdmin } = useAccess()

  const [enabled, setEnabled] = useState(false)
  const [cadence, setCadence] = useState<Cadence>(DEFAULT_CADENCE)
  const [timezone, setTimezone] = useState(BROWSER_TZ)
  const [window, setWindow] = useState<ReportWindow>("last_7d")
  const [channelId, setChannelId] = useState("")
  const [compare, setCompare] = useState<ReportCompare>(DEFAULT_COMPARE)

  // Seed from the saved schedule once it lands.
  useEffect(() => {
    setEnabled(existing?.enabled ?? false)
    setCadence(existing ? fromCron(existing.cron) : DEFAULT_CADENCE)
    setTimezone(existing?.timezone ?? BROWSER_TZ)
    setWindow(existingInput?.window ?? "last_7d")
    setChannelId(existingInput?.slackChannelId ?? "")
    setCompare(existingInput?.compare ?? DEFAULT_COMPARE)
  }, [existing, existingInput])

  const patchCadence = (patch: Partial<Cadence>) =>
    setCadence(prev => ({ ...prev, ...patch }))

  const handleRunNow = async () => {
    if (!existing) return
    try {
      await runScheduleNow(existing.id).unwrap()
      toast.success(
        "Test post started with this notification's SAVED settings — it lands in Slack shortly.",
      )
    } catch {
      toast.error("Could not start the test post. Please try again.")
    }
  }

  const handleSave = async () => {
    const trimmedChannel = channelId.trim()
    if (trimmedChannel && !/^[A-Za-z0-9]{1,32}$/.test(trimmedChannel)) {
      toast.error(
        "Slack channel must be a channel ID like C0123456789 (not a #name).",
      )
      return
    }

    const cron = toCron(cadence)
    const input: SlackNotificationInput = {
      reportId,
      window,
      // The handler can't see its own JobSchedule row, so the zone it resolves
      // the window in has to travel on the input too. Same value, both places.
      timezone,
      compare,
      ...(trimmedChannel ? { slackChannelId: trimmedChannel } : {}),
    }

    try {
      if (!enabled) {
        if (existing) {
          await updateSchedule({ id: existing.id, enabled: false }).unwrap()
          toast.success("Slack notifications turned off for this report.")
        }
        return
      }

      if (existing) {
        // One atomic PATCH — cron, timezone, enabled AND input. Rewriting the
        // input as delete + create would lose the notification outright whenever
        // the create is rejected.
        await updateSchedule({
          id: existing.id,
          enabled: true,
          cron,
          timezone,
          input,
        }).unwrap()
        toast.success("Slack notification saved.")
        return
      }

      await createSchedule({ type: JOB_TYPE, cron, timezone, input }).unwrap()
      toast.success("Slack notification saved.")
    } catch {
      toast.error("Could not save the Slack notification. Please try again.")
    }
  }

  return (
    <section className="mt-system-xl rounded-lg border border-system-blue-700 bg-system-blue-800 text-white">
      <div className="border-b border-system-blue-700 px-system-lg py-system-md">
        <h2 className="text-lg font-semibold">Slack notifications</h2>
        <p className="text-system-gray-300 text-xs">
          Post this report&apos;s totals to Slack on a schedule. The report
          itself has no dates — the window below is what each post covers.
        </p>
      </div>

      <div className="flex flex-col gap-system-lg px-system-lg py-system-md">
        {isFetching && !schedules ? (
          <p className="text-sm text-system-gray-300">Loading…</p>
        ) : (
          <>
            <label className="flex cursor-pointer items-center gap-system-sm">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={enabled}
                onChange={e => setEnabled(e.target.checked)}
              />
              <span className="text-sm">Post this report to Slack</span>
            </label>

            <section className="flex flex-col gap-system-2xs">
              <span className="text-sm font-medium text-system-gray-200">
                How often
              </span>
              <div className="flex flex-wrap items-center gap-system-sm">
                <Select
                  id="custom-report-slack-frequency"
                  options={FREQUENCY_OPTIONS}
                  value={cadence.frequency}
                  disabled={!enabled}
                  onChange={e =>
                    patchCadence({ frequency: e.target.value as Frequency })
                  }
                />
                {cadence.frequency === "weekly" && (
                  <Select
                    id="custom-report-slack-day"
                    options={DAY_OPTIONS}
                    value={String(cadence.dayOfWeek)}
                    disabled={!enabled}
                    onChange={e =>
                      patchCadence({ dayOfWeek: Number(e.target.value) })
                    }
                  />
                )}
                <input
                  id="custom-report-slack-time"
                  type="time"
                  value={cadence.time}
                  disabled={!enabled}
                  onChange={e =>
                    patchCadence({ time: e.target.value || DEFAULT_TIME })
                  }
                  className="input input-bordered h-10 w-32 bg-system-blue-900 py-0 text-sm disabled:opacity-50"
                />
                <Select
                  id="custom-report-slack-timezone"
                  options={TIMEZONE_OPTIONS}
                  value={timezone}
                  disabled={!enabled}
                  onChange={e => setTimezone(e.target.value)}
                />
              </div>
              {cadence.frequency === "monthly" && (
                <p className="text-system-gray-300 text-xs">
                  Posts on the 1st of each month.
                </p>
              )}
              {cadence.frequency === "quarterly" && (
                <p className="text-system-gray-300 text-xs">
                  Posts on 1 Jan, 1 Apr, 1 Jul and 1 Oct.
                </p>
              )}
            </section>

            <section className="flex flex-col gap-system-2xs">
              <label
                htmlFor="custom-report-slack-window"
                className="text-sm font-medium text-system-gray-200"
              >
                Period each post covers
              </label>
              <p className="text-system-gray-300 text-xs">
                Rolling windows end yesterday — today&apos;s data is still
                landing, so including it would understate the last day.
              </p>
              <Select
                id="custom-report-slack-window"
                options={WINDOW_OPTIONS}
                value={window}
                disabled={!enabled}
                onChange={e => setWindow(e.target.value as ReportWindow)}
              />
              <label
                htmlFor="custom-report-slack-compare"
                className="mt-system-sm text-sm font-medium text-system-gray-200"
              >
                Compare against
              </label>
              <p className="text-system-gray-300 text-xs">
                Year-over-year shifts rolling windows by 52 weeks so the
                weekdays line up; a quarter is compared to the same quarter last
                year. Ranked sections show the current period only.
              </p>
              <Select
                id="custom-report-slack-compare"
                options={COMPARE_OPTIONS}
                value={compare}
                disabled={!enabled}
                onChange={e => setCompare(e.target.value as ReportCompare)}
              />
            </section>

            <section className="flex flex-col gap-system-2xs">
              {isSuperAdmin ? (
                <>
                  <label
                    htmlFor="custom-report-slack-channel"
                    className="text-sm font-medium text-system-gray-200"
                  >
                    Slack channel (optional)
                  </label>
                  <p className="text-system-gray-300 text-xs">
                    Channel ID (e.g. C0123456789). Leave empty to use this
                    company&apos;s default channel. The Marathon bot must be
                    invited to the channel.
                  </p>
                  <Input
                    id="custom-report-slack-channel"
                    value={channelId}
                    disabled={!enabled}
                    onChange={e => setChannelId(e.target.value)}
                    placeholder="Defaults to the company channel"
                  />
                </>
              ) : (
                <>
                  <span className="text-sm font-medium text-system-gray-200">
                    Slack channel
                  </span>
                  <p
                    className="text-system-gray-300 text-xs"
                    data-testid="slack-channel-destination"
                  >
                    {channelId
                      ? `Posts to channel ${channelId}. Only an administrator can change it.`
                      : "Posts to this company's default Slack channel."}
                  </p>
                </>
              )}
            </section>
          </>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-system-blue-700 px-system-lg py-system-md">
        {/* Fires the SAVED notification, not the unsaved form values. */}
        {existing ? (
          <button
            type="button"
            onClick={() => void handleRunNow()}
            disabled={isRunningNow || isSaving}
            className="rounded-md border border-system-blue-600 px-system-md py-system-2xs text-sm font-medium text-system-gray-200 hover:bg-system-blue-700 hover:text-white disabled:opacity-50"
            title="Post this report to Slack now with its saved settings"
          >
            {isRunningNow ? "Starting…" : "Send test now"}
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || isFetching}
          className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500 disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </section>
  )
}
